import { TranslateModule } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ChartModule} from 'primeng/chart';
import {SvgIconService} from '../../services/svg-icon.service';

export interface RevenueChartData {
  labels: string[];
  revenue: number[];
  refunds: number[];
}

@Component({
  selector: 'bo-revenue-chart',
  templateUrl: './revenue-chart.component.html',
  styleUrls: ['./revenue-chart.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CommonModule, ChartModule]
})
export class RevenueChartComponent implements OnInit, OnChanges {
  @Input({required: true}) data!: RevenueChartData;
  @Input() title = 'financePages.erChartTitle';
  chartData: any;
  chartOptions: any;
  #svgIconService = inject(SvgIconService);
  #sanitizer = inject(DomSanitizer);
  #cdr = inject(ChangeDetectorRef);
  revenueIcon: SafeHtml = '';
  refundsIcon: SafeHtml = '';

  ngOnInit(): void {
    this.loadIcons();
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.data) {
      this.updateChart();
    }
  }

  private loadIcons(): void {
    this.#svgIconService
      .getSvgContent('assets/icons/revenue-chart.svg')
      .subscribe((svg: string) => {
        this.revenueIcon = this.#sanitizer.bypassSecurityTrustHtml(svg);
        this.#cdr.markForCheck();
      });
    this.#svgIconService
      .getSvgContent('assets/icons/refund-chart.svg')
      .subscribe((svg: string) => {
        this.refundsIcon = this.#sanitizer.bypassSecurityTrustHtml(svg);
        this.#cdr.markForCheck();
      });
  }

  private initChart(): void {
    this.chartOptions = this.buildChartOptions();
    this.updateChart();
  }

  private updateChart(): void {
    if (!this.data) {
      return;
    }

    this.chartData = {
      labels: this.data.labels,
      datasets: [
        {
          label: 'Revenue',
          data: this.data.revenue,
          fill: false,
          borderColor: '#FCA13B',
          backgroundColor: '#FCA13B',
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Refunds',
          data: this.data.refunds,
          fill: false,
          borderColor: '#EF4444',
          backgroundColor: '#EF4444',
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };

    this.#cdr.markForCheck();
  }

  private buildChartOptions() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColorSecondary = documentStyle.getPropertyValue(
      '--text-color-secondary'
    );
    const surfaceBorder =
      documentStyle.getPropertyValue('--surface-border');

    return {
      responsive: true,
      maintainAspectRatio: false,
      height: 400,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: surfaceBorder,
          borderWidth: 1
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
            borderDash: [5, 5]
          }
        },
        y: {
          ticks: {
            color: textColorSecondary,
            callback: function(value: number) {
              return value.toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              });
            }
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
            borderDash: [5, 5]
          }
        }
      }
    };
  }
}

